/**
 * Custom Archive Format for Optimized Document Upload (v1.1)
 *
 * Binary format with GZIP compression and no filename length limits.
 * Used for both folder uploads and parsed document uploads.
 *
 * Format Structure (after GZIP decompression):
 * [Manifest JSON as UTF-8 string]
 * [4-byte manifest length (big-endian)]
 * [File 0 data]
 * [File 1 data]
 * ...
 *
 * The manifest contains:
 * - version: "1.1" (compressed format)
 * - compressed: true
 * - File paths (unlimited length)
 * - File sizes
 * - File offsets (calculated from cumulative sizes)
 * - File MD5 hashes (computed during archive build)
 * - Folder structure metadata
 *
 * For parsed uploads, files are organized:
 * - markdown/ - Markdown files
 * - assets/ - Image files
 * - sheets/ - Spreadsheet files
 */

import SparkMD5 from 'spark-md5';
import { FolderStructure, UploadDecision, UploadMode } from '../types/upload';

/**
 * File entry in manifest
 */
export interface FileEntry {
  path: string;
  size: number;
  type: string;
  lastModified: number;
  offset: number; // Byte offset in archive where file data starts
  md5?: string; // MD5 hash computed during archive build
}

/**
 * Archive manifest
 */
export interface ArchiveManifest {
  version: string;
  timestamp: string;
  compressed: boolean;
  fileCount: number;
  totalSize: number;
  folderStructure: FolderStructure;
  files: FileEntry[];
}

/**
 * Error thrown when a file cannot be read during archive building
 */
export class FileReadError extends Error {
  constructor(
    public readonly fileName: string,
    public readonly originalError: unknown
  ) {
    const errorDetail = originalError instanceof Error ? `: ${originalError.message}` : '';
    let hint = 'File có thể đã bị di chuyển, xóa hoặc đổi tên.';
    if (fileName.length > 200) {
      hint = 'Tên file quá dài. Hãy đổi tên file ngắn hơn (dưới 200 ký tự).';
    }
    const message = `Không thể đọc file "${fileName}"${errorDetail}. ${hint}`;
    super(message);
    this.name = 'FileReadError';
  }
}

/**
 * Progress callback for archive building
 */
export type ArchiveBuildProgressCallback = (progress: number, currentFile: number, totalFiles: number) => void;

/**
 * Custom Archive Builder
 */
export class CustomArchiveBuilder {
  /**
   * Build archive from File array
   *
   * @param files - Array of files to include in archive
   * @param onProgress - Optional progress callback (progress: 0-100, currentFile, totalFiles)
   * @returns Blob containing custom archive
   */
  async buildFromFiles(files: File[], onProgress?: ArchiveBuildProgressCallback): Promise<Blob> {
    console.log(`[CustomArchive] Building archive from ${files.length} files`);

    // Step 1: Read all files, compute MD5 hashes, and calculate offsets
    const fileEntries: FileEntry[] = [];
    const fileBuffers: ArrayBuffer[] = [];
    let currentOffset = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const path = file.webkitRelativePath || file.name;
      const buffer = await file.arrayBuffer();
      fileBuffers.push(buffer);

      // Compute MD5 hash from file content
      const spark = new SparkMD5.ArrayBuffer();
      spark.append(buffer);
      const md5 = spark.end();

      fileEntries.push({
        path,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        offset: currentOffset,
        md5
      });
      currentOffset += file.size;
    }

    // Step 2: Create manifest (includes MD5 hashes)
    const manifest: ArchiveManifest = {
      version: '1.1',
      timestamp: new Date().toISOString(),
      compressed: true,
      fileCount: files.length,
      totalSize: currentOffset,
      folderStructure: this.buildFolderTree(files),
      files: fileEntries
    };

    const manifestJson = JSON.stringify(manifest);
    const manifestBytes = new TextEncoder().encode(manifestJson);
    const manifestLength = manifestBytes.length;

    // Step 3: Create archive buffer
    // Format: [manifest][4-byte length][file data...]
    const archiveSize = manifestLength + 4 + currentOffset;
    const archiveBuffer = new ArrayBuffer(archiveSize);
    const archiveView = new Uint8Array(archiveBuffer);
    const dataView = new DataView(archiveBuffer);

    let writeOffset = 0;

    // Write manifest
    archiveView.set(manifestBytes, writeOffset);
    writeOffset += manifestLength;

    // Write manifest length (4 bytes, big-endian)
    dataView.setUint32(writeOffset, manifestLength, false);
    writeOffset += 4;

    // Step 4: Write all file data with progress reporting
    let lastProgressUpdate = 0;
    const PROGRESS_UPDATE_THRESHOLD = 5; // Report every 5% progress

    for (let i = 0; i < files.length; i++) {
      const fileBytes = new Uint8Array(fileBuffers[i]);
      archiveView.set(fileBytes, writeOffset);
      writeOffset += files[i].size;

      // Calculate progress (0-100%)
      const progress = ((i + 1) / files.length) * 100;
      const isLastFile = i === files.length - 1;

      // Throttle progress updates (every 5% or last file)
      if (onProgress && (isLastFile || progress - lastProgressUpdate >= PROGRESS_UPDATE_THRESHOLD)) {
        onProgress(progress, i + 1, files.length);
        lastProgressUpdate = progress;
      }

      if ((i + 1) % 100 === 0) {
        console.log(`[CustomArchive] Processed ${i + 1}/${files.length} files`);
      }
    }

    console.log(`[CustomArchive] Archive created: ${archiveSize} bytes`);

    return new Blob([archiveBuffer], { type: 'application/octet-stream' });
  }

  /**
   * Build hierarchical folder tree from flat file list
   *
   * @param files - Array of files
   * @returns Root folder structure
   */
  private buildFolderTree(files: File[]): FolderStructure {
    const root: FolderStructure = {
      name: 'root',
      type: 'folder',
      children: [],
      fileCount: 0,
      totalSize: 0
    };

    for (const file of files) {
      const path = (file.webkitRelativePath || file.name).split('/');
      let current = root;

      // Navigate/create folder structure
      for (let i = 0; i < path.length - 1; i++) {
        let folder = current.children?.find((c) => c.type === 'folder' && c.name === path[i]);

        if (!folder) {
          folder = {
            name: path[i],
            type: 'folder',
            children: [],
            fileCount: 0,
            totalSize: 0
          };
          current.children?.push(folder);
        }

        current = folder;
      }

      // Add file to current folder
      const fileName = path[path.length - 1];
      const extension = fileName.includes('.') ? fileName.split('.').pop() || '' : '';

      current.children?.push({
        name: fileName,
        type: 'file',
        size: file.size,
        extension
      });

      // Update counts for current folder
      current.fileCount = (current.fileCount || 0) + 1;
      current.totalSize = (current.totalSize || 0) + file.size;

      // Propagate size to all parent folders
      let parent = root;
      parent.fileCount = (parent.fileCount || 0) + 1;
      parent.totalSize = (parent.totalSize || 0) + file.size;

      for (let i = 0; i < path.length - 1; i++) {
        const nextParent = parent.children?.find((c) => c.type === 'folder' && c.name === path[i]) as FolderStructure;
        if (nextParent && nextParent !== current) {
          nextParent.fileCount = (nextParent.fileCount || 0) + 1;
          nextParent.totalSize = (nextParent.totalSize || 0) + file.size;
        }
        parent = nextParent;
      }
    }

    return root;
  }

  /**
   * Compress a blob using GZIP (native CompressionStream API)
   */
  private async compressBlob(blob: Blob): Promise<Blob> {
    const stream = blob.stream();
    const compressed = stream.pipeThrough(new CompressionStream('gzip'));
    return new Response(compressed).blob();
  }

  /**
   * Detect the common root folder prefix from file paths.
   * When user drops a folder, all files share the same root prefix.
   */
  private detectDroppedFolderPrefix(files: File[]): string | null {
    if (files.length === 0) return null;

    const paths = files.map((f) => f.webkitRelativePath || f.name).filter((p) => p.includes('/'));

    if (paths.length === 0) return null;

    // Get first path segment (the dropped folder name)
    const firstSegment = paths[0].split('/')[0];

    // Verify all files share this prefix
    const allMatch = paths.every((p) => p.startsWith(firstSegment + '/'));

    return allMatch ? firstSegment : null;
  }

  /**
   * Create a virtual file with prefixed path for organized archive structure
   * @param file - The source file
   * @param prefix - Category prefix (e.g., 'markdown/', 'assets/')
   * @param stripPrefix - Optional prefix to strip from original path (dropped folder name)
   */
  private prefixFilePath(file: File, prefix: string, stripPrefix?: string | null): File {
    let originalPath = file.webkitRelativePath || file.name;

    // Strip the dropped folder prefix if provided
    if (stripPrefix && originalPath.startsWith(stripPrefix + '/')) {
      originalPath = originalPath.substring(stripPrefix.length + 1);
    }

    const newPath = prefix + originalPath;

    // Create new File with same content but modified path
    const newFile = new File([file], file.name, {
      type: file.type,
      lastModified: file.lastModified
    });

    // Override webkitRelativePath for archive path calculation
    Object.defineProperty(newFile, 'webkitRelativePath', {
      value: newPath,
      writable: false,
      configurable: true
    });

    return newFile;
  }

  /**
   * Build compressed archive for parsed document upload
   * Organizes files into markdown/, assets/, sheets/ folders
   *
   * @param markdownFiles - Markdown files (.md)
   * @param assetFiles - Asset files (png, jpg, etc.)
   * @param sheetFiles - Spreadsheet files (csv, xlsx, etc.)
   * @param onProgress - Optional progress callback
   * @returns Compressed archive blob
   */
  async buildParsedArchive(
    markdownFiles: File[],
    assetFiles: File[],
    sheetFiles: File[],
    onProgress?: ArchiveBuildProgressCallback
  ): Promise<Blob> {
    console.log(
      `[CustomArchive] Building parsed archive: ${markdownFiles.length} markdown, ` +
        `${assetFiles.length} assets, ${sheetFiles.length} sheets`
    );

    // Detect dropped folder prefix from all content files
    const allFiles = [...markdownFiles, ...assetFiles, ...sheetFiles];
    const droppedFolderPrefix = this.detectDroppedFolderPrefix(allFiles);

    if (droppedFolderPrefix) {
      console.log(`[CustomArchive] Stripping dropped folder prefix: ${droppedFolderPrefix}/`);
    }

    // Organize files with folder prefixes (stripping dropped folder prefix)
    const organizedFiles: File[] = [
      ...markdownFiles.map((f) => this.prefixFilePath(f, 'markdown/', droppedFolderPrefix)),
      ...assetFiles.map((f) => this.prefixFilePath(f, 'assets/', droppedFolderPrefix)),
      ...sheetFiles.map((f) => this.prefixFilePath(f, 'sheets/', droppedFolderPrefix))
    ];

    // Build raw archive (MD5 hashes computed during build)
    const rawArchive = await this.buildFromFiles(organizedFiles, onProgress);

    // Compress with GZIP
    console.log(`[CustomArchive] Compressing archive...`);
    const compressedArchive = await this.compressBlob(rawArchive);
    console.log(
      `[CustomArchive] Compressed: ${rawArchive.size} -> ${compressedArchive.size} bytes ` +
        `(${Math.round((1 - compressedArchive.size / rawArchive.size) * 100)}% reduction)`
    );

    return compressedArchive;
  }

}

/**
 * Upload mode decision thresholds
 */
const ARCHIVE_THRESHOLD = 10; // >=10 files triggers archive mode
const SIZE_THRESHOLD = 10 * 1024 * 1024; // >=10MB triggers archive mode

/**
 * Determines if files should be uploaded as archive
 */
export function shouldUseArchive(files: File[]): boolean {
  if (files.length === 1) {
    return false; // Single file = normal upload
  }

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);
  return files.length >= ARCHIVE_THRESHOLD || totalSize >= SIZE_THRESHOLD;
}

/**
 * Determines upload mode based on file count and size
 */
export function determineUploadMode(files: File[]): UploadDecision {
  const useArchive = shouldUseArchive(files);
  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  let mode: UploadMode;
  if (files.length === 1) {
    mode = 'single'; // Traditional single file upload
  } else if (useArchive) {
    mode = 'archive'; // Custom archive upload
  } else {
    mode = 'folder'; // Traditional folder tree upload
  }

  return {
    mode,
    fileCount: files.length,
    totalSize,
    useArchive
  };
}
